import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ShieldCheck, AlertTriangle, AlertOctagon, RefreshCw, Table2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Compliance Validation — scans for the identity/address gaps that cause a
 * form rejection, plus the clinical data-porting map, before a packet is built.
 */
export default function ComplianceValidation({ year, quarter, includeState, state, onStatus }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [map, setMap] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const run = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/reports/quarterly-packet/validate", {
        params: { year: Number(year), quarter, include_state: includeState, state: state || "" },
      });
      setScan(data);
      onStatus?.(data);
    } catch (e) { setErr(formatApiError(e)); setScan(null); onStatus?.(null); }
    setBusy(false);
  }, [year, quarter, includeState, state, onStatus]);

  useEffect(() => { run(); }, [run]);

  const loadMap = async () => {
    setShowMap((v) => !v);
    if (map) return;
    try {
      const { data } = await api.get("/reports/quarterly-packet/data-map", {
        params: { year: Number(year), quarter },
      });
      setMap(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const Row = ({ f, blocking }) => (
    <div data-testid={`validation-${blocking ? "blocker" : "warning"}-${f.code}`}
         className={"border-2 border-ink p-2.5 " + (blocking ? "bg-warn" : "")}>
      <div className="flex items-start gap-2">
        {blocking ? <AlertOctagon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
        <div className="min-w-0">
          <div className="font-mono text-[11px] font-black">{f.message}</div>
          <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{f.fix}</div>
          {f.forms && <div className="font-mono text-[9px] uppercase tracking-widest mt-1">Affects · {f.forms}</div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="border-2 border-ink p-4" data-testid="compliance-validation">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="overline flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> § Compliance Validation</div>
        <div className="flex items-center gap-2">
          <button onClick={loadMap} data-testid="toggle-data-map"
                  className="border-2 border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-secondary">
            <Table2 className="w-3 h-3" /> {showMap ? "Hide" : "Data porting"}
          </button>
          <button onClick={run} disabled={busy} data-testid="rescan-compliance"
                  className="border-2 border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-secondary disabled:opacity-40">
            <RefreshCw className={"w-3 h-3 " + (busy ? "animate-spin" : "")} /> {busy ? "Scanning" : "Re-scan"}
          </button>
        </div>
      </div>

      {err && <div data-testid="validation-error" className="font-mono text-[11px] mb-2">{err}</div>}

      {scan && (
        <>
          <div data-testid="validation-status"
               className={"font-mono text-xs p-2.5 border-2 border-ink mb-3 " + (scan.ok ? "bg-ink text-white" : "bg-warn")}>
            {scan.ok
              ? `Clear — ${scan.employees_checked} employee record(s) paid in Q${quarter} carry a valid SSN and a complete address.`
              : `${scan.blocker_count} blocker(s) would cause a form rejection. The packet is held until they are fixed.`}
            {scan.warning_count > 0 && ` · ${scan.warning_count} warning(s) to review.`}
          </div>

          <div className="space-y-2">
            {scan.blockers.map((f, i) => <Row key={`b${i}`} f={f} blocking />)}
            {scan.warnings.map((f, i) => <Row key={`w${i}`} f={f} />)}
          </div>

          <div className="font-mono text-[10px] text-muted-foreground mt-3 leading-relaxed">{scan.note}</div>
        </>
      )}

      {showMap && map && (
        <div className="mt-4" data-testid="data-porting-map">
          <div className="overline mb-2">Ported to the forms · Q{quarter} {year}</div>
          <div className="overflow-x-auto border-2 border-ink">
            <table className="w-full font-mono text-[11px]">
              <thead className="bg-ink text-white">
                <tr>
                  {["Employee", "SSN", "Address", "State", "Gross (941 L2)", "Fed WH (941 L3)", "State WH"].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 uppercase text-[9px] tracking-widest whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {map.employees.map((r) => (
                  <tr key={r.employee_id} className="border-t border-ink/20"
                      data-testid={`porting-row-${r.employee_id}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.last_name}, {r.first_name} {r.middle_initial}</td>
                    <td className="px-2 py-1.5">{r.ssn_masked || <span className="font-black">MISSING</span>}</td>
                    <td className="px-2 py-1.5">{r.address_complete ? r.address : <span className="font-black">INCOMPLETE</span>}</td>
                    <td className="px-2 py-1.5">{r.work_state || "—"}</td>
                    <td className="px-2 py-1.5 text-right">{money(r.gross)}</td>
                    <td className="px-2 py-1.5 text-right">{money(r.federal_withheld)}</td>
                    <td className="px-2 py-1.5 text-right">{money(r.state_withheld)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-ink font-black" data-testid="porting-totals">
                  <td className="px-2 py-1.5" colSpan={4}>{map.totals.employees} employee(s)</td>
                  <td className="px-2 py-1.5 text-right">{money(map.totals.gross)}</td>
                  <td className="px-2 py-1.5 text-right">{money(map.totals.federal_withheld)}</td>
                  <td className="px-2 py-1.5 text-right">{money(map.totals.state_withheld)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-3 space-y-1">
            <div className="overline">Field map</div>
            {map.field_map.map((m) => (
              <div key={m.source} className="font-mono text-[10px] flex flex-wrap gap-1">
                <span className="font-black">{m.source}</span><span>→</span><span className="text-muted-foreground">{m.target}</span>
              </div>
            ))}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-2">{map.note}</div>
        </div>
      )}
    </div>
  );
}
